import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LoadingState } from '../../loading/loadable';
import RenderAsync from '../common/RenderAsync';
import H from '../elements/H';
import Spacing from '../common/Spacing';
import Row from '../grid/Row';
import Col from '../grid/Col';
import { baseXUnit, color, fontSize } from '../../styles/theme';
import Icon from '../common/Icon';
import { configuration } from '../../utils/environment';

interface MemoryHotelTabProps {
  hotelIds: string[];
}

interface HotelData {
  id: number;
  title: string;
  excerpt: string;
  fullContent: string;
  imageUrl?: string;
  ratingImt?: string;
  imtCount?: string;
  hotelStars?: string;
  link?: string;
}

const StarBadge: React.FC<{ stars: string }> = ({ stars }) => {
  const starCount = parseInt(stars);
  if (isNaN(starCount)) return null;

  const textMap: Record<number, string> = {
    1: 'Yhden tähden hotelli',
    2: 'Kahden tähden hotelli',
    3: 'Kolmen tähden hotelli',
    4: 'Neljän tähden hotelli',
    5: 'Viiden tähden hotelli'
  };

  return (
    <div className="star-badge">
      {textMap[starCount] || `${starCount} tähden hotelli`}
      <style jsx>{`
        .star-badge {
          display: inline-block;
          background: ${color.lightGray};
          color: ${color.dark};
          padding: ${baseXUnit(0.5)} ${baseXUnit(1.5)};
          border-radius: ${baseXUnit(0.5)};
          font-size: ${fontSize.px12};
          font-weight: bold;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: ${baseXUnit(1)};
        }
      `}</style>
    </div>
  );
};

const RatingBox: React.FC<{ rating: string; count: string }> = ({
  rating,
  count
}) => {
  if (!rating) return null;

  return (
    <div className="rating-box">
      <div className="rating-score">{rating}</div>
      <div className="rating-info">
        <div className="rating-label">IMT.fi asiakasarviot</div>
        <div className="rating-count">{count} arvostelua</div>
      </div>
      <style jsx>{`
        .rating-box {
          display: flex;
          align-items: center;
          gap: ${baseXUnit(1.5)};
          margin-bottom: ${baseXUnit(2)};
        }
        .rating-score {
          background: ${color.brand};
          color: ${color.white};
          font-weight: bold;
          font-size: ${fontSize.px20};
          padding: ${baseXUnit(1)};
          border-radius: 8px;
          min-width: 44px;
          text-align: center;
        }
        .rating-label {
          font-weight: bold;
          font-size: ${fontSize.px14};
          color: ${color.dark};
        }
        .rating-count {
          font-size: ${fontSize.px12};
          color: ${color.gray};
        }
      `}</style>
    </div>
  );
};

export const MemoryHotelTab: React.FC<MemoryHotelTabProps> = ({ hotelIds }) => {
  const [loadingState, setLoadingState] = useState(LoadingState.STARTED);
  const [hotels, setHotels] = useState<HotelData[]>([]);
  const [expandedHotels, setExpandedHotels] = useState<Record<number, boolean>>(
    {}
  );

  const toggleExpand = (id: number) => {
    setExpandedHotels((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    let active = true;
    const fetchHotels = async () => {
      try {
        const promises = hotelIds.map((id) =>
          axios
            .get(
              `${configuration.imtWebshopUrl}/wp-json/wp/v2/imt_hotel_v2/${id.trim()}?_embed`
            )
            .then((res) => {
              const data = res.data;
              return {
                id: data.id,
                title: data.title?.rendered,
                excerpt: data.excerpt?.rendered,
                fullContent: data.content?.rendered,
                imageUrl: data._embedded?.['wp:featuredmedia']?.[0]?.source_url,
                ratingImt: data.acf?.rating_imt,
                imtCount: data.acf?.imt_count,
                hotelStars: data.acf?.hotel_stars,
                link: data.link
              };
            })
        );
        const results = await Promise.all(promises);
        if (active) {
          setHotels(results);
          setLoadingState(LoadingState.DONE);
        }
      } catch (error) {
        if (active) {
          console.error('Failed to fetch hotels', error);
          setLoadingState(LoadingState.FAILED);
        }
      }
    };

    if (hotelIds.length > 0) {
      fetchHotels();
    } else {
      setLoadingState(LoadingState.DONE);
    }

    return () => {
      active = false;
    };
  }, [hotelIds]);

  return (
    <div className="hotel-tab">
      <RenderAsync
        state={loadingState}
        onLoading={() => (
          <div className="loading-state">Haetaan hotelleja...</div>
        )}
      >
        {hotels.length === 0 ? (
          <p className="no-hotels">Ei hotelleja saatavilla tälle matkalle.</p>
        ) : (
          <div className="hotel-list">
            {hotels.map((hotel) => {
              const isExpanded = expandedHotels[hotel.id];
              return (
                <div key={hotel.id} className="hotel-card">
                  <Row>
                    {hotel.imageUrl && (
                      <Col size={[24, 24, 10]}>
                        <div className="image-wrapper">
                          <img
                            src={hotel.imageUrl}
                            alt={hotel.title}
                            className="hotel-image"
                          />
                        </div>
                      </Col>
                    )}
                    <Col size={[24, 24, hotel.imageUrl ? 14 : 24]}>
                      <div className="content-inner">
                        {hotel.hotelStars && (
                          <StarBadge stars={hotel.hotelStars} />
                        )}
                        <Spacing bottom={2}>
                          <H styling="h3">
                            <strong
                              dangerouslySetInnerHTML={{ __html: hotel.title }}
                            />
                          </H>
                        </Spacing>

                        <RatingBox
                          rating={hotel.ratingImt || ''}
                          count={hotel.imtCount || ''}
                        />

                        <div
                          className={`hotel-description ${isExpanded ? 'expanded' : ''}`}
                          dangerouslySetInnerHTML={{
                            __html: isExpanded
                              ? hotel.fullContent
                              : hotel.excerpt
                          }}
                        />

                        <div className="button-group">
                          <button
                            className="read-more-btn"
                            onClick={() => toggleExpand(hotel.id)}
                          >
                            {isExpanded ? 'Näytä vähemmän' : 'Näytä lisää'}
                            <Icon
                              name={
                                isExpanded ? 'nav-arrow-up' : 'nav-arrow-down'
                              }
                              size={1.5}
                            />
                          </button>

                          {hotel.link && (
                            <a
                              href={hotel.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="external-link-btn"
                            >
                              Tutustu lisää hotelliin IMT.fi -sivustolla
                              <Icon
                                name="nav-arrow-right"
                                size={1.5}
                                color="white"
                              />
                            </a>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <div className="hotel-separator" />
                </div>
              );
            })}
          </div>
        )}
      </RenderAsync>

      <style jsx>{`
        .hotel-tab {
          width: 100%;
        }
        .hotel-card {
          margin-bottom: ${baseXUnit(4)};
        }
        .image-wrapper {
          position: relative;
          width: 100%;
          padding-top: 66%; /* 3:2 aspect ratio */
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }
        .hotel-image {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
        .hotel-card:hover .hotel-image {
          transform: scale(1.03);
        }
        .content-inner {
          padding: 0 ${baseXUnit(2)};
        }
        .loading-state,
        .no-hotels {
          font-size: ${fontSize.px16};
          color: ${color.gray};
          padding: 24px 0;
          text-align: center;
        }
        .hotel-description {
          font-size: ${fontSize.px16};
          line-height: 1.6;
          color: ${color.dark};
          margin-bottom: ${baseXUnit(3)};
          max-height: 120px;
          overflow: hidden;
          position: relative;
          transition: max-height 0.4s ease;
        }
        .hotel-description.expanded {
          max-height: 2000px;
        }
        .hotel-description:not(.expanded)::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 40px;
          background: linear-gradient(transparent, ${color.white});
        }
        .hotel-description :global(p) {
          margin-bottom: 16px;
        }
        .button-group {
          display: flex;
          flex-wrap: wrap;
          gap: ${baseXUnit(2)};
          margin-top: ${baseXUnit(1)};
        }
        .read-more-btn {
          background: none;
          border: none;
          color: ${color.brand};
          font-weight: bold;
          font-size: ${fontSize.px14};
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: ${baseXUnit(1)};
          padding: 0;
          transition: opacity 0.2s;
        }
        .read-more-btn:hover {
          opacity: 0.8;
        }
        .external-link-btn {
          display: flex;
          align-items: center;
          gap: ${baseXUnit(1)};
          background: ${color.brand};
          color: ${color.white};
          padding: ${baseXUnit(1.5)} ${baseXUnit(2.5)};
          border-radius: 30px;
          text-decoration: none;
          font-weight: bold;
          font-size: ${fontSize.px14};
          transition:
            transform 0.2s,
            background-color 0.2s;
        }
        .external-link-btn:hover {
          background: ${color.brand};
          opacity: 0.9;
          transform: translateY(-2px);
        }
        .hotel-separator {
          height: 1px;
          background: ${color.lightGray};
          margin: ${baseXUnit(6)} 0;
        }
        .hotel-card:last-child .hotel-separator {
          display: none;
        }
        @media (max-width: 768px) {
          .content-inner {
            padding: ${baseXUnit(3)} 0 0 0;
          }
          .button-group {
            flex-direction: column;
            align-items: flex-start;
          }
          .external-link-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};
